'use client'
import { useTranslations } from 'next-intl'
import { Tooltip } from 'primitives/tooltip'
import type { PublicUserProfile } from 'codegen/types'
import { IconCheckCircle } from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'
import { Z_INDEX_MODALS } from 'constants/modals'
import { useUserTypeQuery } from '@repo/cf-user/hooks/use-user-type-query'
import { useIsSelfQuery } from '@repo/cf-user/hooks/use-is-self-query'

export const VerifiedPremiumRibbon = ({ user }: { user: PublicUserProfile }) => {
  const { data: type } = useUserTypeQuery({ userSlug: user?.slug ?? '' })
  const { data: isSelf } = useIsSelfQuery({ userSlug: user?.slug ?? '' })

  if (!type) {
    return null
  }

  const icon =
    // oxlint-disable-next-line no-nested-ternary
    type === 'both' ? (
      <IconCheckCircle className="flex-shrink-0 text-cf-blue-700 outline outline-2 outline-offset-0 rounded-full outline-cf-yellow-700 inline-block size-[14px]" />
    ) : type === 'verified' ? (
      <IconCheckCircle className="flex-shrink-0 text-cf-blue-700 rounded-full inline-block size-[14px]" />
    ) : (
      <IconCheckCircle className="flex-shrink-0 text-cf-yellow-700 rounded-full inline-block size-[14px]" />
    )

  return (
    <Tooltip
      delay={0}
      value={<TooltipContent type={type} isSelf={isSelf} />}
      side="bottom"
      hideArrow
      className="bg-transparent p-0 z-[10000]"
      sideOffset={6}
    >
      <div
        className={tw(
          'inline-flex justify-center items-center ml-1',
          type === 'both' ? 'ml-1.5' : 'ml-1',
        )}
      >
        {icon}
      </div>
    </Tooltip>
  )
}

export function TooltipContent({
  type,
  isSelf,
}: {
  type?: 'verified' | 'premium' | 'both'
  isSelf?: boolean
}) {
  const t = useTranslations('pages.clubhouse.user')

  const tooltipTextSelf =
    // oxlint-disable-next-line no-nested-ternary
    type === 'both'
      ? t('verified_premium_tooltip_text_self')
      : // oxlint-disable-next-line no-nested-ternary
        type === 'verified'
        ? t('verified_tooltip_text_self')
        : // oxlint-disable-next-line unicorn/no-nested-ternary
          type === 'premium'
          ? t('premium_tooltip_text_self')
          : ''

  const tooltipTextUser =
    // oxlint-disable-next-line no-nested-ternary
    type === 'both'
      ? t('verified_premium_tooltip_text')
      : // oxlint-disable-next-line no-nested-ternary
        type === 'verified'
        ? t('verified_tooltip_text')
        : // oxlint-disable-next-line unicorn/no-nested-ternary
          type === 'premium'
          ? t('premium_tooltip_text')
          : ''

  const tooltipText = isSelf ? tooltipTextSelf : tooltipTextUser

  if (!tooltipText) {
    return null
  }

  return (
    <div
      style={{ zIndex: Z_INDEX_MODALS.default }}
      className="relative bg-cf-white max-w-[247px] rounded-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.15),_0_1px_3px_1px_rgba(0,0,0,0.15)] border border-[#0A0A0B1A]"
    >
      <div
        aria-hidden
        className="absolute left-1/2 -top-[3px] w-[10px] h-[10px] -translate-x-1/2 rotate-45 bg-cf-white pointer-events-none shadow-[0_1px_2px_0_rgba(0,0,0,0.15),_0_1px_3px_1px_rgba(0,0,0,0.15)] z-0 border border-[#0A0A0B1A]"
      />
      <div className="relative z-10 bg-cf-white p-2 rounded-2">
        <p className="text-cf-grey-800 text-12 text-left font-normal">{tooltipText}</p>
      </div>
    </div>
  )
}
